import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, StatusPill, Select } from "@/components/kit";
import { Txt } from "@/components/Txt";
import { useQuickAdd } from "@/lib/quickAdd";
import ConfirmDelete from "@/components/ConfirmDelete";

const fmtDT = (s) => (s ? new Date(s).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const AUDIENCE_LABEL = { everyone: "Everyone", board: "Board only", tag: "Tag" };
const inputCls = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]";

async function downloadCsv(eventId, kind, testid) {
  try {
    const r = await api.get(`/events/${eventId}/export/${kind}`, { responseType: "blob" });
    const cd = r.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const name = m ? m[1] : `${kind}.csv`;
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  } catch (e) { toast.error("Could not export CSV"); }
}

const ExportBtn = ({ eventId, kind, label, testid }) => (
  <Button variant="secondary" data-testid={testid} onClick={() => downloadCsv(eventId, kind, testid)} className="min-h-[38px] py-1.5 text-[13px]">
    <Icon name="Download" className="w-3.5 h-3.5" /> {label}
  </Button>
);

const Field = ({ label, children }) => (
  <div><label className="text-[13px] font-semibold block mb-1">{label}</label>{children}</div>
);

const RsvpRow = ({ rk, label, count, names, open, setOpen }) => (
  <div className="border-b border-ink/8 last:border-0">
    <button data-testid={`rsvp-toggle-${rk}`} onClick={() => setOpen(open === rk ? null : rk)} className="w-full flex items-center justify-between py-2.5 text-left">
      <span className="text-[15px] font-semibold">{label}</span>
      <span className="flex items-center gap-2"><span className="text-[15px] font-semibold" data-testid={`rsvp-count-${rk}`}>{count}</span>{names && names.length > 0 && <Icon name="ChevronDown" className={`w-4 h-4 transition-transform ${open === rk ? "rotate-180" : ""}`} />}</span>
    </button>
    {open === rk && names && (
      <div className="pb-2 space-y-1" data-testid={`rsvp-names-${rk}`}>
        {names.length === 0 ? <div className="text-[13px] text-ink/50">No names.</div> : names.map((n, i) => <div key={i} className="text-[14px] text-ink/70">{n}</div>)}
      </div>
    )}
  </div>
);

const empty = { title: "", description: "", start: "", end: "", address: "", video_link: "", tags: "", audience_type: "everyone", audience_tag: "", status: "draft" };

function FlyerUploader({ eventId, flyer, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [big, setBig] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image.");
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { const r = await api.post(`/events/${eventId}/flyer`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Flyer uploaded"); onUploaded(r.data.flyer_url); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-4 mt-3" data-testid="event-flyer">
      <label className="text-[13px] font-semibold block mb-2">Event flyer / media</label>
      {flyer && (
        <button type="button" data-testid="flyer-thumb" onClick={() => setBig(true)} className="block mb-2 rounded-lg overflow-hidden border border-ink/15 hover:opacity-90">
          <img src={flyer} alt="Event flyer" className="max-h-56 w-auto object-contain bg-warm-cream" />
        </button>
      )}
      <label className="inline-flex items-center gap-1.5 bg-ink text-cream rounded-lg px-4 py-2 min-h-[42px] text-[14px] font-semibold cursor-pointer">
        <Icon name="Upload" className="w-4 h-4" /> {busy ? "Uploading…" : (flyer ? "Replace flyer" : "Upload flyer")}
        <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="flyer-input" />
      </label>
      <p className="text-[12px] text-ink/50 mt-1">Click the flyer to view it full screen. JPG or PNG, up to 15 MB.</p>
      {big && flyer && createPortal(
        <div className="fixed inset-0 z-[120] bg-ink/90 flex items-center justify-center p-4" onClick={() => setBig(false)} data-testid="flyer-lightbox">
          <img src={flyer} alt="Event flyer" className="max-h-full max-w-full object-contain" />
          <button data-testid="flyer-lightbox-close" onClick={() => setBig(false)} className="absolute top-4 right-4 text-cream bg-ink/60 rounded-full w-10 h-10 flex items-center justify-center"><Icon name="X" className="w-5 h-5" /></button>
        </div>, document.body)}
    </div>
  );
}

function CheckinControl({ eventId }) {
  const [active, setActive] = useState(null);
  const load = () => api.get("/checkin/sessions/active").then((r) => setActive(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const isThis = active?.active && active.event_id === eventId;
  const start = async () => { try { await api.post("/checkin/sessions/start", { event_id: eventId }); toast.success("Check-in started"); window.dispatchEvent(new CustomEvent("rca:navigate", { detail: { section: "checkin" } })); } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); } };
  const stop = async () => { try { await api.post(`/checkin/sessions/${active.session_id}/close`); toast.success("Check-in stopped"); load(); } catch (e) { toast.error("Could not stop"); } };
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-4 mt-3" data-testid="event-checkin-control">
      <h4 className="font-zilla text-lg font-semibold mb-2">Check-in</h4>
      {isThis ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[14px] flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-olive animate-pulse" /> Live for this event</span>
          <button data-testid="event-stop-checkin" onClick={stop} className="border border-maroon/40 text-maroon rounded-lg px-4 py-2 min-h-[42px] text-[14px] font-semibold">Stop check-in</button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[13px] text-ink/60">{active?.active ? `Station is live for "${active.event_title || "General"}". Starting here switches it.` : "Open the shared-QR station for this event."}</span>
          <Button data-testid="event-start-checkin" onClick={start} className="px-4 py-2 min-h-[42px] text-[14px]">Start Check-In</Button>
        </div>
      )}
    </div>
  );
}

function AttendancePanel({ eventId }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/events/${eventId}/attendance`).then((r) => setA(r.data)).catch(() => {}); }, [eventId]);
  if (!a) return <div className="text-ink/55 text-[14px] py-4">Loading…</div>;
  return (
    <div data-testid="attendance-panel" className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <ExportBtn eventId={eventId} kind="attendance" label="Export attendance" testid="export-attendance-csv" />
        <ExportBtn eventId={eventId} kind="no-shows" label="Export no-shows" testid="export-no-shows-csv" />
      </div>
      <div className="flex gap-3">
        <div className="flex-1 bg-cream border border-ink/15 rounded-xl p-3 text-center"><div className="font-zilla text-2xl font-semibold" data-testid="att-member-count">{a.member_count}</div><div className="text-[12px] text-ink/55 uppercase tracking-wide">Members</div></div>
        <div className="flex-1 bg-cream border border-ink/15 rounded-xl p-3 text-center"><div className="font-zilla text-2xl font-semibold" data-testid="att-visitor-count">{a.visitor_count}</div><div className="text-[12px] text-ink/55 uppercase tracking-wide">Visitors</div></div>
      </div>
      {[["Members", a.members, true], ["Visitors", a.visitors, false]].map(([label, rows, isMember]) => (
        <div key={label}>
          <h5 className="text-[13px] font-semibold uppercase tracking-wide text-ink/55 mb-1">{label}</h5>
          {rows.length === 0 ? <div className="text-[13px] text-ink/45 mb-2">None recorded.</div> : rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between text-[14px] border-b border-ink/8 py-1.5 last:border-0">
              <span>{r.name}{r.member_number ? ` · ${r.member_number}` : ""}</span>
              <span className="flex items-center gap-2 text-[12px] text-ink/50">via {r.source || "station"}{isMember && r.walk_in && <span className="px-2 py-0.5 rounded-full bg-gold/30 text-ink font-semibold" data-testid="walk-in-flag">walk-in</span>}</span>
            </div>
          ))}
        </div>
      ))}
      {a.no_shows.length > 0 && (
        <div>
          <h5 className="text-[13px] font-semibold uppercase tracking-wide text-maroon mb-1">RSVP'd going but didn't check in</h5>
          {a.no_shows.map((n, i) => <div key={i} data-testid="no-show-row" className="text-[14px] text-ink/70 py-1">{n.name} <span className="text-[12px] text-maroon font-semibold">no-show</span></div>)}
        </div>
      )}
    </div>
  );
}

function RoleVisibility({ vis, setVis, eventAudience, idx }) {
  const v = vis || { type: "event" };
  const [q, setQ] = useState(""); const [res, setRes] = useState([]);
  useEffect(() => {
    if (v.type !== "individuals" || !q.trim()) { setRes([]); return; }
    let live = true;
    const t = setTimeout(async () => { try { const r = await api.get("/membership/directory", { params: { q, limit: 8 } }); if (live) setRes((r.data.members || []).filter((m) => m.user_id)); } catch { /* noop */ } }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, v.type]);
  const people = v.user_ids || [];
  return (
    <div className="bg-warm-cream/60 rounded-lg p-2 space-y-2" data-testid={`role-vis-${idx}`}>
      <div className="flex items-center gap-2">
        <label className="text-[12px] text-ink/55 shrink-0">Who can see this role</label>
        <Select data-testid={`role-vis-type-${idx}`} value={v.type} onChange={(e) => setVis({ type: e.target.value, tag: "", user_ids: [], names: [] })} className={`${inputClsSm} flex-1`}>
          <option value="event">Everyone the event is for</option>
          {eventAudience === "everyone" && <option value="everyone">Everyone</option>}
          <option value="board">Board members only</option>
          <option value="tag">A specific tag</option>
          <option value="individuals">Hand-picked people</option>
        </Select>
      </div>
      {v.type === "tag" && <input list="event-tag-list" data-testid={`role-vis-tag-${idx}`} value={v.tag || ""} onChange={(e) => setVis({ ...v, tag: e.target.value })} placeholder="Tag (e.g. Gardener)" className={`${inputClsSm} w-full`} />}
      {v.type === "individuals" && (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-1">
            {people.map((uid, k) => (
              <span key={uid} data-testid={`role-vis-person-${idx}-${uid}`} className="inline-flex items-center gap-1 text-[12px] bg-olive/20 border border-olive/40 rounded-full px-2 py-0.5">
                {(v.names || [])[k] || "Person"}
                <button onClick={() => setVis({ ...v, user_ids: people.filter((x) => x !== uid), names: (v.names || []).filter((_, j) => j !== k) })} className="text-ink/50">×</button>
              </span>
            ))}
          </div>
          <input data-testid={`role-vis-search-${idx}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people to add…" className={`${inputClsSm} w-full`} />
          {res.length > 0 && (
            <div className="border border-ink/15 rounded-lg bg-cream max-h-40 overflow-y-auto">
              {res.map((m) => (
                <button key={m.user_id} data-testid={`role-vis-opt-${m.user_id}`} onClick={() => { if (!people.includes(m.user_id)) setVis({ ...v, user_ids: [...people, m.user_id], names: [...(v.names || []), m.name] }); setQ(""); setRes([]); }} className="w-full text-left px-2 py-1.5 border-b border-ink/8 last:border-0 hover:bg-warm-cream/60 text-[13px]">{m.name}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VolunteerEditor({ roles, setRoles, eventAudience }) {
  const add = () => setRoles([...(roles || []), { role_id: "", name: "", start: "", end: "", needed: 1, points: 0, visibility: { type: "event" } }]);
  const upd = (i, k, v) => setRoles(roles.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rm = (i) => setRoles(roles.filter((_, idx) => idx !== i));
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-4 mt-3" data-testid="volunteer-editor">
      <div className="flex items-center justify-between mb-2"><h4 className="font-zilla text-lg font-semibold">Volunteer roles</h4>
        <button data-testid="add-role" onClick={add} className="text-[13px] font-semibold text-dark-olive">+ Add role</button></div>
      {(roles || []).length === 0 && <p className="text-[13px] text-ink/50">No roles yet. Add Setup Crew, Food Table, Cleanup, etc.</p>}
      {(roles || []).map((r, i) => (
        <div key={i} className="border-b border-ink/8 py-2 last:border-0 space-y-2" data-testid={`role-edit-${i}`}>
          <div className="flex gap-2">
            <input data-testid={`role-name-${i}`} placeholder="Role name" value={r.name} onChange={(e) => upd(i, "name", e.target.value)} className={`${inputClsSm} flex-1`} />
            <input type="number" min="1" data-testid={`role-needed-${i}`} value={r.needed} onChange={(e) => upd(i, "needed", parseInt(e.target.value) || 1)} className={`${inputClsSm} w-20`} />
            <button data-testid={`role-remove-${i}`} onClick={() => rm(i)} className="text-maroon px-2"><Icon name="X" className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-2">
            <input type="datetime-local" data-testid={`role-start-${i}`} value={r.start ? r.start.slice(0, 16) : ""} onChange={(e) => upd(i, "start", e.target.value)} className={`${inputClsSm} flex-1`} />
            <input type="datetime-local" data-testid={`role-end-${i}`} value={r.end ? r.end.slice(0, 16) : ""} onChange={(e) => upd(i, "end", e.target.value)} className={`${inputClsSm} flex-1`} />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[12px] text-ink/55">Points for showing up</label>
            <input type="number" min="0" data-testid={`role-points-${i}`} value={r.points ?? 0} onChange={(e) => upd(i, "points", parseInt(e.target.value) || 0)} className={`${inputClsSm} w-24`} />
          </div>
          <RoleVisibility idx={i} vis={r.visibility} eventAudience={eventAudience} setVis={(vis) => upd(i, "visibility", vis)} />
        </div>
      ))}
    </div>
  );
}

const inputClsSm = "bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]";

const STATUS_BADGE = { yes: "bg-olive/25 text-ink", no: "bg-maroon/20 text-maroon", awaiting: "bg-gold/30 text-ink" };

function MessagingPanel({ eventId, tags }) {
  const [detail, setDetail] = useState(null);
  const [ann, setAnn] = useState({ audience: "all", tag: "", note: "" });
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState([]);
  const [msg, setMsg] = useState("");
  const [mkt, setMkt] = useState("");
  const load = () => api.get(`/events/${eventId}`).then((r) => setDetail(r.data)).catch(() => {});
  useEffect(() => { load(); }, [eventId]);
  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); return; }
    const t = setTimeout(() => api.get("/events/member-accounts", { params: { q } }).then((r) => setResults(r.data.members)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);
  const sendAnn = async () => { try { const r = await api.post(`/events/${eventId}/announce`, ann); toast.success(`Announcement sent to ${r.data.recipients}`); setAnn({ ...ann, note: "" }); } catch (e) { toast.error("Could not send"); } };
  const add = (m) => { if (!picked.find((p) => p.user_id === m.user_id)) setPicked([...picked, m]); setQ(""); setResults([]); };
  const sendInvite = async () => { if (!picked.length) return; try { await api.post(`/events/${eventId}/invite`, { user_ids: picked.map((p) => p.user_id), message: msg }); toast.success("Invitations sent"); setPicked([]); setMsg(""); load(); } catch (e) { toast.error("Could not invite"); } };
  const sendMkt = async () => { try { const r = await api.post(`/events/${eventId}/notify-marketing`, { note: mkt }); toast.success(`Sent to ${r.data.recipients} Marketing Director(s)`); setMkt(""); } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); } };

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-4 mt-3 space-y-5" data-testid="messaging-panel">
      <div>
        <h4 className="font-zilla text-lg font-semibold mb-2">Announcement</h4>
        <div className="flex gap-2 mb-2">
          <Select data-testid="announce-audience" value={ann.audience} onChange={(e) => setAnn({ ...ann, audience: e.target.value })} className={inputClsSm}><option value="all">Everyone</option><option value="tag">A tag</option></Select>
          {ann.audience === "tag" && <input list="msg-tags" data-testid="announce-tag" value={ann.tag} onChange={(e) => setAnn({ ...ann, tag: e.target.value })} placeholder="Tag" className={`${inputClsSm} flex-1`} />}
          <datalist id="msg-tags">{(tags || []).map((t) => <option key={t} value={t} />)}</datalist>
        </div>
        <textarea data-testid="announce-note" rows={2} value={ann.note} onChange={(e) => setAnn({ ...ann, note: e.target.value })} placeholder="Custom note (event details are included automatically)" className={`${inputClsSm} w-full mb-2`} />
        <Button data-testid="announce-send" onClick={sendAnn}>Send announcement</Button>
      </div>

      <div className="border-t border-ink/10 pt-4">
        <h4 className="font-zilla text-lg font-semibold mb-2">Targeted invitations</h4>
        <input data-testid="invite-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members with an account" className={`${inputClsSm} w-full mb-1`} />
        {results.length > 0 && <div className="border border-ink/15 rounded-lg mb-2 max-h-40 overflow-y-auto">{results.map((m) => <button key={m.user_id} data-testid={`invite-opt-${m.user_id}`} onClick={() => add(m)} className="w-full text-left px-3 py-2 text-[14px] hover:bg-warm-cream">{m.name} <span className="text-ink/45 text-[12px]">{m.member_number}</span></button>)}</div>}
        {picked.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">{picked.map((p) => <span key={p.user_id} className="text-[12px] bg-ink text-cream rounded-full px-2 py-1">{p.name} <button onClick={() => setPicked(picked.filter((x) => x.user_id !== p.user_id))}>×</button></span>)}</div>}
        <textarea data-testid="invite-message" rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Custom message (e.g. Can you help with setup?)" className={`${inputClsSm} w-full mb-2`} />
        <Button variant="olive" data-testid="invite-send" onClick={sendInvite} disabled={!picked.length}>Send invitations</Button>
        {detail?.invitations?.length > 0 && (
          <div className="mt-3 space-y-1" data-testid="invitation-statuses">
            {detail.invitations.map((i) => (
              <div key={i.user_id} data-testid={`inv-status-${i.user_id}`} className="flex items-center justify-between text-[14px] py-1 border-b border-ink/8 last:border-0">
                <span>{i.name}</span>
                <span className={`text-[12px] px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_BADGE[i.status] || ""}`}>{i.status === "awaiting" ? "awaiting response" : i.status}</span>              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-ink/10 pt-4">
        <h4 className="font-zilla text-lg font-semibold mb-2">Notify the Marketing Director</h4>
        <p className="text-[12px] text-ink/55 mb-2">Goes to whoever currently holds the Marketing Director role.</p>
        <textarea data-testid="marketing-note" rows={2} value={mkt} onChange={(e) => setMkt(e.target.value)} placeholder="Note for the Marketing Director" className={`${inputClsSm} w-full mb-2`} />
        <Button data-testid="marketing-send" onClick={sendMkt}>Send to Marketing Director</Button>
      </div>
    </div>
  );
}

function EventForm({ initial, tags, canEdit, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    ...empty, ...initial,
    tags: Array.isArray(initial?.tags) ? initial.tags.join(", ") : (initial?.tags || ""),
    start: initial?.start ? initial.start.slice(0, 16) : "",
    end: initial?.end ? initial.end.slice(0, 16) : "",
    volunteer_roles: initial?.volunteer_roles || [],
  }));
  const isEdit = !!initial?.event_id;
  const [detailTab, setDetailTab] = useState("rsvps");
  const [flyer, setFlyer] = useState(initial?.flyer_url || "");
  const [confirmDel, setConfirmDel] = useState(false);

  const save = async (status) => {
    const body = {
      title: f.title, description: f.description, start: f.start, end: f.end || null,
      address: f.address, video_link: f.video_link, flyer_url: flyer,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      audience_type: f.audience_type, audience_tag: f.audience_type === "tag" ? f.audience_tag : null,
      status: status || f.status, volunteer_roles: f.volunteer_roles,
    };
    try {
      if (isEdit) await api.put(`/events/${initial.event_id}`, body);
      else await api.post("/events", body);
      toast.success(isEdit ? "Event saved" : "Event created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save event"); }
  };
  const del = async () => {
    try { await api.delete(`/events/${initial.event_id}`); toast.success("Event moved to Trash"); onSaved(); }
    catch (e) { toast.error("Could not delete"); }
  };
  const closeEvent = async () => {
    if (!window.confirm("Move this event to Past Events now? Members will no longer be able to RSVP.")) return;
    try { await api.post(`/events/${initial.event_id}/close`); toast.success("Event moved to Past Events"); onSaved(); }
    catch (e) { toast.error("Could not close event"); }
  };
  const reopenEvent = async () => {
    try { await api.post(`/events/${initial.event_id}/reopen`); toast.success("Event reopened"); onSaved(); }
    catch (e) { toast.error("Could not reopen"); }
  };
  const isPast = !!initial?.is_past;

  return (
    <div data-testid="event-form">
      <button onClick={onClose} data-testid="event-form-close" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-ink/70 hover:text-ink mb-3">
        <Icon name="ChevronLeft" className="w-4 h-4" /> Back to events
      </button>
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-4">{isEdit ? "Edit event" : "New event"}</h1>
      <div className="max-w-3xl space-y-3">
          <Field label="Title"><input data-testid="event-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} /></Field>
          <Field label="Description"><textarea data-testid="event-description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Starts"><input type="datetime-local" data-testid="event-start" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={inputCls} /></Field>
            <Field label="Ends (optional)"><input type="datetime-local" data-testid="event-end" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className={inputCls} /></Field>
          </div>
          <Field label="Address (optional)"><input data-testid="event-address" placeholder="123 Main St, Chicago, IL" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} /></Field>
          <Field label="Video call link (optional)"><input data-testid="event-video" placeholder="https://…" value={f.video_link} onChange={(e) => setF({ ...f, video_link: e.target.value })} className={inputCls} /></Field>
          <Field label="Tags (comma-separated)"><input data-testid="event-tags" placeholder="fundraiser, family" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className={inputCls} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Audience">
              <Select data-testid="event-audience" value={f.audience_type} onChange={(e) => setF({ ...f, audience_type: e.target.value })} className={inputCls}>
                <option value="everyone">Everyone</option>
                <option value="board">Board only</option>
                <option value="tag">Specific tag</option>
              </Select>
            </Field>
            {f.audience_type === "tag" && (
              <Field label="Which tag">
                <input list="event-tag-list" data-testid="event-audience-tag" value={f.audience_tag} onChange={(e) => setF({ ...f, audience_tag: e.target.value })} className={inputCls} placeholder="e.g. Gardener" />
                <datalist id="event-tag-list">{tags.map((t) => <option key={t} value={t} />)}</datalist>
              </Field>
            )}
          </div>
          {canEdit && (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="secondary" data-testid="event-save-draft" onClick={() => save("draft")}>Save as draft</Button>
              <Button data-testid="event-publish" onClick={() => save("published")}>{f.status === "published" ? "Save & keep published" : "Publish"}</Button>
              {isEdit && <Button variant="danger" data-testid="event-delete" onClick={() => setConfirmDel(true)} className="ml-auto">Delete</Button>}
            </div>
          )}
          <ConfirmDelete open={confirmDel} onClose={() => setConfirmDel(false)} onConfirm={del} title="Delete this event?" message="This moves the event to Trash, where it can be restored for 30 days before it is permanently removed." />
          {canEdit && <VolunteerEditor roles={f.volunteer_roles} setRoles={(r) => setF({ ...f, volunteer_roles: r })} eventAudience={f.audience_type} />}
          {canEdit && (isEdit
            ? <FlyerUploader eventId={initial.event_id} flyer={flyer} onUploaded={setFlyer} />
            : <p className="text-[13px] text-ink/50 mt-2" data-testid="flyer-hint">Save the event first, then you can upload a flyer.</p>)}
          {isEdit && isPast && (
            <div className="mt-4">
              <RecapPanel eventId={initial.event_id} />
              {canEdit && initial?.closed_at && (
                <div className="mt-3">
                  <Button variant="secondary" data-testid="event-reopen" onClick={reopenEvent}>Reopen event</Button>
                  <p className="text-[12px] text-ink/50 mt-1">Reopening moves it back to Upcoming and lets members RSVP again.</p>
                </div>
              )}
            </div>
          )}
          {isEdit && !isPast && (
            <>
              <div className="mt-3">
                <div className="flex gap-2 mb-2">
                  <button data-testid="detail-tab-rsvps" onClick={() => setDetailTab("rsvps")} className={`rounded-lg px-3 py-1.5 text-[13px] font-semibold ${detailTab === "rsvps" ? "bg-ink text-cream" : "border border-ink/20"}`}>RSVPs</button>
                  <button data-testid="detail-tab-attendance" onClick={() => setDetailTab("attendance")} className={`rounded-lg px-3 py-1.5 text-[13px] font-semibold ${detailTab === "attendance" ? "bg-ink text-cream" : "border border-ink/20"}`}>Actual Attendance</button>
                </div>
                {detailTab === "rsvps" ? <RsvpBreakdown eventId={initial.event_id} /> : <AttendancePanel eventId={initial.event_id} />}
              </div>
              {initial?.status === "published" && canEdit && <CheckinControl eventId={initial.event_id} />}
              {initial?.status === "published" && canEdit && (
                <div className="bg-cream border border-ink/15 rounded-xl p-4 mt-3" data-testid="event-close-control">
                  <h4 className="font-zilla text-lg font-semibold mb-1">Wrap up this event</h4>
                  <p className="text-[13px] text-ink/60 mb-2">Finished early or need to close it out? Move it to Past Events now to show the recap.</p>
                  <Button variant="secondary" data-testid="event-close" onClick={closeEvent}>Close event</Button>
                </div>
              )}
              {canEdit && <MessagingPanel eventId={initial.event_id} tags={tags} />}
            </>
          )}
        </div>
      </div>
  );
}

function RsvpBreakdown({ eventId }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get(`/events/${eventId}`).then((r) => setD(r.data)).catch(() => {}); }, [eventId]);
  if (!d) return null;
  const c = d.rsvp_counts || {};
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-4 mt-3" data-testid="rsvp-breakdown">
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-zilla text-lg font-semibold">RSVPs</h4>
        <ExportBtn eventId={eventId} kind="rsvps" label="Export RSVPs" testid="export-rsvps-csv" />
      </div>
      <p className="text-[12px] text-ink/50 mb-2">Audience size: {d.audience_total ?? "—"}</p>
      <RsvpRow rk="going" label="Going" count={c.going || 0} names={d.going_names} open={open} setOpen={setOpen} />
      <RsvpRow rk="cant" label="Can't make it" count={c.cant || 0} names={d.cant_names} open={open} setOpen={setOpen} />
      <RsvpRow rk="no_reply" label="No reply" count={c.no_reply ?? 0} names={null} open={open} setOpen={setOpen} />
    </div>
  );
}

function RecapPanel({ eventId }) {
  const [d, setD] = useState(null);
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get(`/events/${eventId}`).then((r) => setD(r.data)).catch(() => {});
    api.get(`/events/${eventId}/attendance`).then((r) => setA(r.data)).catch(() => {});
  }, [eventId]);
  const openMember = (mid) => {
    if (!mid) return;
    sessionStorage.setItem("rca_open_member", mid);
    window.dispatchEvent(new CustomEvent("rca:navigate", { detail: { section: "membership_directory" } }));
  };
  const NameRow = ({ name, memberId, sub, tone, testid }) => (
    memberId ? (
      <button data-testid={testid} onClick={() => openMember(memberId)} className="w-full flex items-center justify-between text-left border-b border-ink/8 py-1.5 last:border-0 hover:bg-warm-cream/60 rounded">
        <span className="text-[14px] text-dark-olive font-medium underline decoration-dotted">{name}</span>
        {sub && <span className={`text-[12px] ${tone || "text-ink/50"}`}>{sub}</span>}
      </button>
    ) : (
      <div data-testid={testid} className="flex items-center justify-between border-b border-ink/8 py-1.5 last:border-0">
        <span className="text-[14px]">{name}</span>
        {sub && <span className={`text-[12px] ${tone || "text-ink/50"}`}>{sub}</span>}
      </div>
    )
  );
  if (!d || !a) return <div className="text-ink/55 text-[14px] py-4" data-testid="recap-loading">Loading recap…</div>;
  const c = d.rsvp_counts || {};
  const totalIn = (a.member_count || 0) + (a.visitor_count || 0);
  return (
    <div data-testid="event-recap" className="space-y-4">
      <div className="bg-ink text-cream rounded-xl p-5" data-testid="recap-hero">
        <div className="text-[12px] uppercase tracking-widest font-bold text-gold">Event recap</div>
        <div className="font-zilla text-5xl font-semibold leading-none my-1.5" data-testid="recap-checkedin">{totalIn}</div>
        <div className="text-[15px] text-cream/80">checked in — {a.member_count} member{a.member_count === 1 ? "" : "s"} and {a.visitor_count} visitor{a.visitor_count === 1 ? "" : "s"}</div>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-4" data-testid="recap-rsvp-breakdown">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h4 className="font-zilla text-lg font-semibold">RSVP breakdown</h4>
          <div className="flex gap-2">
            <ExportBtn eventId={eventId} kind="rsvps" label="RSVPs" testid="recap-export-rsvps" />
            <ExportBtn eventId={eventId} kind="attendance" label="Attendance" testid="recap-export-attendance" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-olive/15 py-2"><div className="font-zilla text-2xl font-semibold" data-testid="recap-going">{c.going || 0}</div><div className="text-[12px] text-ink/60">Going</div></div>
          <div className="rounded-lg bg-maroon/10 py-2"><div className="font-zilla text-2xl font-semibold" data-testid="recap-cant">{c.cant || 0}</div><div className="text-[12px] text-ink/60">Can't make it</div></div>
          <div className="rounded-lg bg-ink/8 py-2"><div className="font-zilla text-2xl font-semibold" data-testid="recap-noreply">{c.no_reply ?? 0}</div><div className="text-[12px] text-ink/60">No reply</div></div>
        </div>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-4">
        <h4 className="font-zilla text-lg font-semibold mb-2">Who showed up <span className="text-[13px] font-normal text-ink/55">({a.member_count})</span></h4>
        {a.members.length === 0 ? <div className="text-[13px] text-ink/45">No member check-ins recorded.</div> : a.members.map((r, i) => (
          <NameRow key={i} name={`${r.name}${r.member_number ? ` · ${r.member_number}` : ""}`} memberId={r.member_id}
                   sub={r.walk_in ? "walk-in" : "RSVP'd going"} tone={r.walk_in ? "text-ink font-semibold" : "text-dark-olive"} testid={`recap-attendee-${i}`} />
        ))}
        {a.visitors.length > 0 && (
          <div className="mt-3">
            <div className="text-[12px] uppercase tracking-wide text-ink/45 font-bold mb-1">Visitors ({a.visitor_count})</div>
            {a.visitors.map((r, i) => <NameRow key={i} name={r.name} sub={`via ${r.source || "station"}`} testid={`recap-visitor-${i}`} />)}
          </div>
        )}
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-4" data-testid="recap-no-shows">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h4 className="font-zilla text-lg font-semibold">Said going, didn't check in <span className="text-[13px] font-normal text-ink/55">({a.no_shows.length})</span></h4>
          <ExportBtn eventId={eventId} kind="no-shows" label="No-shows" testid="recap-export-no-shows" />
        </div>
        {a.no_shows.length === 0 ? <div className="text-[13px] text-ink/45">Everyone who RSVP'd going checked in.</div> : a.no_shows.map((r, i) => (
          <NameRow key={i} name={r.name} memberId={r.member_id} sub="no-show" tone="text-maroon font-semibold" testid={`recap-noshow-${i}`} />
        ))}
      </div>
    </div>
  );
}

export default function EventsAdmin({ me }) {
  const canEdit = me?.user?.is_super_admin || ["edit", "full"].includes(me?.permissions?.events);
  const [data, setData] = useState({ items: [], total: 0 });
  const [tags, setTags] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [bucket, setBucket] = useState("upcoming");
  const [editing, setEditing] = useState(null); // event object or {} for new
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const canEditEvents = me?.user?.is_super_admin || ["edit", "full"].includes(me?.permissions?.events);
  useQuickAdd("events", () => { if (canEditEvents) setEditing({}); });
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    const params = { limit, skip, bucket };
    if (statusFilter) params.status = statusFilter;
    const r = await api.get("/events", { params });
    setData(r.data);
  }, [statusFilter, skip, bucket]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/events/member-tags").then((r) => setTags(r.data.tags || [])).catch(() => {}); }, []);
  useEffect(() => {
    const eid = sessionStorage.getItem("rca_open_event");
    if (eid) { sessionStorage.removeItem("rca_open_event"); api.get(`/events/${eid}`).then((r) => setEditing(r.data)).catch(() => {}); }
  }, []);

  if (editing) return <EventForm initial={editing.event_id ? editing : null} tags={tags} canEdit={canEdit} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div data-testid="events-admin-page">
      <ConfirmDelete open={confirmDel} onClose={() => setConfirmDel(false)} onConfirm={async () => {
        const ids = [...selected];
        await Promise.all(ids.map((id) => api.delete(`/events/${id}`).catch(() => null)));
        toast.success(`${ids.length} event${ids.length === 1 ? "" : "s"} moved to Trash`);
        setSelected(new Set()); load();
      }} title={`Delete ${selected.size} event${selected.size === 1 ? "" : "s"}?`} message="This moves the selected events to Trash, where they can be restored for 30 days before they are permanently removed." />
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold"><Txt id="events.admin.title" screen="Events admin">Events</Txt></h1>
        {canEdit && <Button data-testid="new-event-btn" onClick={() => setEditing({})}><Icon name="Plus" className="w-4 h-4" /><Txt id="events.admin.new" screen="Events admin">New event</Txt></Button>}
      </div>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="events.admin.subtitle" screen="Events admin">Create events and publish them to the members' calendar. Drafts stay hidden until you publish.</Txt></p>

      <div className="flex gap-2 mb-3" data-testid="events-bucket-tabs">
        {[["upcoming", "Upcoming"], ["past", "Past Events"]].map(([b, lbl]) => (
          <button key={b} data-testid={`events-bucket-${b}`} onClick={() => { setBucket(b); setSkip(0); }}
            className={`rounded-lg px-4 py-2 min-h-[40px] text-[14px] font-semibold ${bucket === b ? "bg-maroon text-cream" : "border border-ink/20"}`}>{lbl}</button>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        {["", "draft", "published"].map((s) => (
          <button key={s || "all"} data-testid={`events-filter-${s || "all"}`} onClick={() => { setStatusFilter(s); setSkip(0); }}
            className={`rounded-lg px-3 py-2 min-h-[38px] text-[13px] font-semibold capitalize ${statusFilter === s ? "bg-ink text-cream" : "border border-ink/20"}`}>{s || "All"}</button>
        ))}
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="events-list">
        {selected.size > 0 && (
          <div className="flex items-center gap-3 bg-ink text-cream px-4 py-3" data-testid="events-bulk-bar">
            <span className="text-[14px] font-semibold">{selected.size} selected</span>
            {canEdit && <Button variant="danger" data-testid="events-bulk-delete" onClick={() => setConfirmDel(true)} className="px-3 py-2 min-h-[38px] text-[13px] !text-cream !border-cream/40">Delete</Button>}
            <button data-testid="events-bulk-clear" onClick={() => setSelected(new Set())} className="text-[13px] underline text-cream/80 ml-auto">Clear</button>
          </div>
        )}
        {data.items.length === 0 && <div className="p-10 text-center text-ink/55"><Txt id="events.admin.empty" screen="Events admin">No events yet.</Txt></div>}
        {data.items.map((ev) => (
          <div key={ev.event_id} className="flex items-center gap-2 pl-4 pr-2 border-b border-ink/8 last:border-0 hover:bg-warm-cream/60">
            {canEdit && <input type="checkbox" data-testid={`event-select-${ev.event_id}`} checked={selected.has(ev.event_id)} onChange={() => toggleSel(ev.event_id)} className="w-4 h-4 accent-maroon shrink-0" />}
            <button data-testid={`event-row-${ev.event_id}`} onClick={() => setEditing(ev)} className="flex-1 min-w-0 flex items-center justify-between gap-3 py-4 text-left">
              <div className="min-w-0">
                <div className="text-[16px] font-semibold flex items-center gap-2 flex-wrap">
                  {ev.title}
                  <StatusPill tone={ev.status === "published" ? "olive" : "gold"}>{ev.status}</StatusPill>
                  {ev.is_past && <StatusPill tone="gray">{ev.closed_at ? "Closed" : "Past"}</StatusPill>}
                </div>
                <div className="text-[13px] text-ink/55">{fmtDT(ev.start)} · {AUDIENCE_LABEL[ev.audience_type]}{ev.audience_tag ? `: ${ev.audience_tag}` : ""} · {ev.is_past ? "View recap" : `Going ${ev.rsvp_counts?.going || 0}`}</div>
              </div>
              <Icon name="ChevronRight" className="w-5 h-5 text-ink/30 shrink-0" />
            </button>
          </div>
        ))}
      </div>

      {data.total > limit && (
        <div className="flex items-center justify-between mt-3 text-[14px]">
          <Button variant="secondary" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Previous</Button>
          <span className="text-ink/60">{skip + 1}–{Math.min(skip + limit, data.total)} of {data.total}</span>
          <Button variant="secondary" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>Next</Button>
        </div>
      )}
    </div>
  );
}
